import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/ApiService';
import PageLoader from '../ui/PageLoader';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import {
  deletePassportEncounterMedia,
  getPassportEncounterDetail,
  getPassportFlags,
  savePassportEncounterMemory,
  submitPassportScanAppeal,
  type PassportEncounterDetail as EncounterDetailData,
  type PassportEncounterMedia,
  type PassportFeatureFlags,
} from '../../services/api/passport';
import PassportEncounterMediaThumb from './PassportEncounterMediaThumb';
import {
  SUBJECT_TYPE_LABELS,
  encounterStatusLabel,
  formatEncounterDateTime,
  METHOD_LABELS,
  riskStatusLabel,
  verificationLevelLabel,
} from './passportCopy';
import { PassportChrome } from './PassportChrome';

const PassportEncounterDetailPage: React.FC = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [flags, setFlags] = useState<PassportFeatureFlags | null>(null);
  const [flagsLoading, setFlagsLoading] = useState(true);
  const [encounter, setEncounter] = useState<EncounterDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [visibility, setVisibility] = useState<'private' | 'public'>('private');
  const [savingMemory, setSavingMemory] = useState(false);
  const [appealReason, setAppealReason] = useState('');
  const [appealOpen, setAppealOpen] = useState(false);
  const [submittingAppeal, setSubmittingAppeal] = useState(false);
  const [media, setMedia] = useState<PassportEncounterMedia[]>([]);
  const [mediaBusy, setMediaBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const f = await getPassportFlags();
      if (!cancelled) {
        setFlags(f);
        setFlagsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    if (!publicId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getPassportEncounterDetail(publicId);
      setEncounter(data);
      setMedia(data.media ?? []);
      setCaption(data.memory_caption || '');
      setVisibility(data.memory_visibility || 'private');
      void apiService.trackEvent('passport_encounter_opened', {
        metadata: { subject_type: data.subject_type, status: data.status },
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Encounter not found');
    } finally {
      setLoading(false);
    }
  }, [publicId]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/passport/encounters/${publicId || ''}` }, replace: true });
      return;
    }
    void load();
  }, [authLoading, isAuthenticated, navigate, publicId, load]);

  const handleSaveMemory = async () => {
    if (!publicId) return;
    setSavingMemory(true);
    try {
      await savePassportEncounterMemory(publicId, { caption, visibility });
      showToast('Memory saved.', 'success');
      void apiService.trackEvent('passport_memory_saved', { metadata: { visibility } });
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to save memory', 'error');
    } finally {
      setSavingMemory(false);
    }
  };

  const handleSubmitAppeal = async () => {
    if (!publicId || !appealReason.trim()) return;
    setSubmittingAppeal(true);
    try {
      await submitPassportScanAppeal(publicId, appealReason.trim());
      showToast('Appeal submitted for review.', 'success');
      void apiService.trackEvent('passport_appeal_submitted');
      setAppealOpen(false);
      setAppealReason('');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to submit appeal', 'error');
    } finally {
      setSubmittingAppeal(false);
    }
  };

  const handlePhotoDelete = async (mediaId: number) => {
    if (!publicId) return;
    setMediaBusy(true);
    try {
      await deletePassportEncounterMedia(publicId, mediaId);
      setMedia((prev) => prev.filter((m) => m.id !== mediaId));
      showToast('Photo removed.', 'success');
      void load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to remove photo', 'error');
    } finally {
      setMediaBusy(false);
    }
  };

  if (authLoading || flagsLoading || loading) {
    return <PageLoader message="Loading encounter…" />;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (error || !encounter) {
    return (
      <PassportChrome
        active="overview"
        flags={flags}
        width="md"
        back={{ label: 'Passport', to: '/passport' }}
        title={
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">
            Encounter
          </h1>
        }
      >
        <EmptyState
          title="Couldn't load this encounter"
          description={error || 'This encounter may have been removed or is not yours.'}
          action={
            <Button type="button" variant="primary" onClick={() => void load()}>
              Try again
            </Button>
          }
        />
      </PassportChrome>
    );
  }

  const canAppeal = encounter.status === 'rejected' || encounter.status === 'revoked';
  const encounterTitle = encounter.subject?.title || SUBJECT_TYPE_LABELS[encounter.subject_type];

  return (
    <PassportChrome
      active="overview"
      flags={flags}
      width="md"
      back={{ label: 'Passport', to: '/passport' }}
      title={
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">
            {encounterTitle}
          </h1>
          <p className="mt-2 text-sm text-zinc-400">
            {SUBJECT_TYPE_LABELS[encounter.subject_type]} · {formatEncounterDateTime(encounter.occurred_at)}
          </p>
        </div>
      }
    >
      {encounter.subject_type === 'vehicle' && encounter.status === 'verified' && <Link className="btn-secondary mb-5" to={`/passport/car?entry=vehicle:${encounter.subject_id}`}>Open car and add a memory</Link>}
      <div className="card p-5 space-y-4 mb-6">
        <dl className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-meta">Status</dt>
            <dd className="text-white mt-1">{encounterStatusLabel(encounter.status)}</dd>
          </div>
          <div>
            <dt className="text-meta">Verification</dt>
            <dd className="text-white mt-1">{verificationLevelLabel(encounter.verification_level)}</dd>
          </div>
          <div>
            <dt className="text-meta">Method</dt>
            <dd className="text-white mt-1">{METHOD_LABELS[encounter.method] || encounter.method}</dd>
          </div>
          <div>
            <dt className="text-meta">Risk review</dt>
            <dd className="text-white mt-1">{riskStatusLabel(encounter.risk_status)}</dd>
          </div>
        </dl>

        {encounter.status === 'pending' ? (
          <div className="alert-warning">
            This encounter is awaiting validation. We'll never claim it happened until it's confirmed.
          </div>
        ) : null}
        {encounter.status === 'rejected' || encounter.status === 'revoked' ? (
          <div className="alert-error">
            This encounter wasn't verified. If you believe this is a mistake, you can appeal below.
          </div>
        ) : null}
      </div>

      <div className="form-section mb-6">
        <h2 className="form-section-title">Archived photos</h2>
        <p className="text-xs text-gray-500 mb-3">
          Your existing photos stay private. New Passport cards are collected through QR.
        </p>
        {media.length > 0 ? (
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
            {media.map((item) => (
              <li key={item.id} className="relative rounded-xl overflow-hidden border border-white/10 bg-black/30">
                {publicId ? (
                  <PassportEncounterMediaThumb encounterPublicId={publicId} mediaId={item.id} />
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute top-1 right-1 bg-black/60"
                  disabled={mediaBusy}
                  onClick={() => void handlePhotoDelete(item.id)}
                  aria-label="Remove photo"
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500 mb-3">No archived photos for this encounter.</p>
        )}
      </div>

      <div className="form-section mb-6">
        <h2 className="form-section-title">Private memory</h2>
        <p className="text-xs text-gray-500">
          Optional. Only visible to you unless you choose to make it public.
        </p>
        <textarea
          className="input input-textarea w-full"
          rows={3}
          maxLength={500}
          placeholder="What do you remember about this one?"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
        />
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              className="checkbox-control"
              checked={visibility === 'public'}
              onChange={(e) => setVisibility(e.target.checked ? 'public' : 'private')}
            />
            Make this note public
          </label>
        </div>
        <Button type="button" variant="primary" size="sm" loading={savingMemory} onClick={() => void handleSaveMemory()}>
          Save memory
        </Button>
      </div>

      {canAppeal ? (
        <div className="form-section">
          <h2 className="form-section-title">Appeal this decision</h2>
          {!appealOpen ? (
            <Button type="button" variant="secondary" size="sm" onClick={() => setAppealOpen(true)}>
              Start an appeal
            </Button>
          ) : (
            <>
              <textarea
                className="input input-textarea w-full"
                rows={3}
                placeholder="Tell us why you think this should be reconsidered."
                value={appealReason}
                onChange={(e) => setAppealReason(e.target.value)}
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  loading={submittingAppeal}
                  disabled={!appealReason.trim()}
                  onClick={() => void handleSubmitAppeal()}
                >
                  Submit appeal
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setAppealOpen(false)}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      ) : null}
    </PassportChrome>
  );
};

export default PassportEncounterDetailPage;
